import { AnvilIcon } from "@/components/icons/AnvilIcon";
import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { X, Send, Loader2, Globe, ChevronDown } from "lucide-react";

function screenName(path) {
  if (path.startsWith("/deals/")) return "Deal detail";
  if (path.startsWith("/pipeline")) return "Pipeline";
  if (path.startsWith("/proposals/federal")) return "Federal Proposals";
  if (path.startsWith("/proposals/commercial")) return "Commercial Proposals";
  if (path.startsWith("/pos")) return "Purchase Orders";
  if (path.startsWith("/contacts")) return "Contacts";
  if (path.startsWith("/company")) return "Company Profile";
  return "Command dashboard";
}

export default function Copilot({ open, setOpen }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(() => {
    try { return localStorage.getItem("anvil-model") || localStorage.getItem("bruh-model") || ""; } catch { return ""; }
  });
  const location = useLocation();
  const endRef = useRef(null);

  const dealId = location.pathname.startsWith("/deals/") ? location.pathname.split("/deals/")[1] : "";
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  useEffect(() => {
    api.get("/copilot/models").then((r) => {
      setModels(r.data.models || []);
      setModel((m) => m || r.data.models?.[1]?.id || r.data.models?.[0]?.id || "");
    }).catch(() => {});
  }, []);
  useEffect(() => { try { if (model) localStorage.setItem("anvil-model", model); } catch { /* ignore */ } }, [model]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const history = msgs.map((m) => ({ role: m.role, content: m.content }));
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setInput(""); setBusy(true);
    try {
      const r = await api.post("/copilot", { message: text, screen: screenName(location.pathname), deal_id: dealId, history, model });
      setMsgs((m) => [...m, { role: "assistant", content: r.data.reply, actions: r.data.actions || [] }]);
      if ((r.data.actions || []).some((a) => a.ok) && dealId) {
        window.dispatchEvent(new CustomEvent("ironclad-refresh"));
      }
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: e?.response?.data?.detail || "Sorry, I hit an error." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      {!open && (
        <button data-testid="copilot-open" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 h-12 pl-3 pr-4 rounded-full bg-zinc-900 text-white shadow-xl hover:scale-105 active:scale-95 transition-transform">
          <span className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center"><AnvilIcon className="h-5 w-5 text-gold" /></span>
          <span className="font-display font-bold text-sm tracking-wide">Anvil</span>
        </button>
      )}

      {open && (
        <aside data-testid="copilot-panel"
          className="fixed top-0 right-0 h-full w-[420px] max-w-[92vw] z-[60] bg-card border-l border-border shadow-2xl flex flex-col fade-in">
            <div className="flex items-center justify-between px-4 py-3 bg-zinc-950 text-white shrink-0">
              <div className="flex items-center gap-2.5">
                <span className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center"><AnvilIcon className="h-5 w-5 text-gold" /></span>
                <div className="leading-tight">
                  <div className="font-display font-bold text-base tracking-wide">Anvil</div>
                  <div className="text-[9px] tracking-[0.14em] text-zinc-400 flex items-center gap-1">
                    <Globe className="h-2.5 w-2.5" /> WEB SEARCH ON
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <select data-testid="copilot-model-select" value={model} onChange={(e) => setModel(e.target.value)}
                    className="appearance-none bg-zinc-800 text-zinc-100 text-[11px] rounded-md pl-2.5 pr-6 py-1.5 outline-none border border-zinc-700 cursor-pointer">
                    {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                  </select>
                  <ChevronDown className="h-3 w-3 text-zinc-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <button data-testid="copilot-close" onClick={() => setOpen(false)}><X className="h-5 w-5 text-zinc-400 hover:text-white" /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 flex flex-col" data-testid="copilot-messages">
              {msgs.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-4" data-testid="copilot-intro">
                  <span className="h-16 w-16 rounded-full bg-gold/15 flex items-center justify-center mb-4"><AnvilIcon className="h-9 w-9 text-gold" /></span>
                  <div className="font-display font-extrabold text-3xl tracking-tight text-foreground">Yo, I'm Anvil.</div>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-3 max-w-xs">
                    Your Ironclad copilot. I act on this screen, search the web for pricing and agency intel,
                    and switch models up top. I confirm before big moves and follow the role rules. What are we doing?
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {msgs.map((m, i) => (
                    <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
                      <div className={`inline-block px-3 py-2 rounded-lg max-w-[88%] whitespace-pre-wrap ${
                        m.role === "user" ? "bg-zinc-900 text-white" : "bg-muted text-foreground"}`}>{m.content}</div>
                      {(m.actions || []).filter((a) => a.ok).length > 0 && (
                        <div className="mt-1 text-[10px] text-emerald-600">✓ {m.actions.filter((a) => a.ok).map((a) => a.tool).join(", ")}</div>
                      )}
                    </div>
                  ))}
                  {busy && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Anvil is thinking…</div>}
                  <div ref={endRef} />
                </div>
              )}
            </div>

            <div className="p-3 border-t border-border flex items-center gap-2 shrink-0">
              <input data-testid="copilot-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Anvil…"
                className="flex-1 bg-background border border-input rounded-md px-3 py-2 text-sm text-foreground outline-none focus:border-zinc-500" />
              <button data-testid="copilot-send" onClick={send} disabled={busy}
                className="h-9 w-9 rounded-md bg-zinc-900 text-white flex items-center justify-center disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </div>
        </aside>
      )}
    </>
  );
}
